// src/features/dashboard/utils/dashboard.time.js

const TZ = 'Africa/Nairobi'

// ─── Core helper ───────────────────────────────────────────────────────────────
// Returns the current date parts in EAT using the Intl API (no offset arithmetic)
function getEATDateParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-KE', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)

  return {
    year:  Number(parts.find(p => p.type === 'year').value),
    month: Number(parts.find(p => p.type === 'month').value),
    day:   Number(parts.find(p => p.type === 'day').value),
  }
}

// Builds a UTC ISO string for midnight EAT on a given y/m/d
function eatMidnightToISO(year, month, day) {
  // "midnight in Nairobi" = UTC-3h, so we find the UTC instant that
  // corresponds to 00:00:00 Africa/Nairobi on that date.
  // The reliable way: binary-search is overkill; just use the offset
  // the Intl API tells us for that specific instant.
  //
  // Simpler approach: construct a date string the browser parses as
  // local, then shift by the actual EAT offset for that moment.
  //
  // Most reliable zero-dependency approach:
  const candidate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
  // candidate is UTC midnight; EAT midnight is 3 h earlier in UTC
  return new Date(candidate.getTime() - 3 * 60 * 60 * 1000).toISOString()
}

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns today's date string in EAT as "YYYY-MM-DD".
 * Safe regardless of browser timezone.
 */
export function getTodayStartEAT() {
  const { year, month, day } = getEATDateParts()
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` 
}

/**
 * Converts a "YYYY-MM-DD" date key to a UTC ISO string representing
 * noon EAT on that day (safe for DB storage, avoids day-boundary drift).
 */
export function toTransactionDateEAT(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number)
  // Noon EAT = 09:00 UTC — well clear of any boundary
  return new Date(Date.UTC(year, month - 1, day, 9, 0, 0)).toISOString()
}

/**
 * Returns { start, end } UTC ISO strings for the requested period in EAT.
 * period: 'today' | 'week' | 'month'
 */
export function getPeriodRange(period = 'today') {
  const now = new Date()
  const { year, month, day } = getEATDateParts(now)

  let startYear = year, startMonth = month, startDay = day
  let endYear   = year, endMonth   = month, endDay   = day

  if (period === 'week') {
    // Monday-based week
    const jsDay = new Date(Date.UTC(year, month - 1, day)).getDay() // 0=Sun
    const daysFromMonday = (jsDay + 6) % 7
    const monday = new Date(Date.UTC(year, month - 1, day - daysFromMonday))
    const { year: sy, month: sm, day: sd } = getEATDateParts(monday)
    startYear = sy; startMonth = sm; startDay = sd
  } else if (period === 'month') {
    startDay = 1
  }

  // Start = 00:00 EAT = 21:00 UTC previous day
  const start = eatMidnightToISO(startYear, startMonth, startDay)
  // End   = 23:59:59.999 EAT = add 24h to start of end day minus 1ms
  const endMidnight = new Date(eatMidnightToISO(endYear, endMonth, endDay))
  const end = new Date(endMidnight.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString()

  return { start, end }
}

/**
 * Formats a date/ISO string for display in EAT.
 * Pass Intl.DateTimeFormat options as the second argument.
 */
export function formatDateEAT(dateStr, options = {}) {
  if (!dateStr) return ''
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr
  return new Intl.DateTimeFormat('en-KE', { timeZone: TZ, ...options }).format(date)
}